import { useCallback, useEffect, useRef, useState } from 'react';

import { createActionPlan } from '../../../api/climateActionPlanApi';
import {
  dismissAiSource,
  generateAiRun,
  getAiRun,
  undoDismissAiSource,
} from '../../../api/climateAiRunApi';
import { normalizeActionPlan } from '../components/actionPlanModel';

const isCanceled = error => (
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
);

/**
 * Load and mutate the AI recommendation run of one fiscal year (P7).
 *
 * The run is fetched whenever the fiscal year changes. Generation replaces the
 * run only after the backend validates the LLM output, so a failed call keeps
 * the previous results visible. Saving a recommendation as a draft action and
 * dismissing/restoring sources all refresh the run afterwards so the saved and
 * dismissed markers stay in sync with the backend.
 */
export default function useClimateAiRun(selectedYear, enabled = true) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const sequenceRef = useRef(0);

  const load = useCallback(() => {
    const controller = new AbortController();
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    setLoading(true);
    setError(null);

    getAiRun(selectedYear, { signal: controller.signal })
      .then((payload) => {
        if (sequenceRef.current === sequence) setRun(payload);
      })
      .catch((loadError) => {
        if (sequenceRef.current === sequence && !isCanceled(loadError)) {
          setError(loadError);
        }
      })
      .finally(() => {
        if (sequenceRef.current === sequence) setLoading(false);
      });

    return controller;
  }, [selectedYear]);

  useEffect(() => {
    if (!enabled) {
      sequenceRef.current += 1;
      setRun(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const controller = load();
    return () => controller.abort();
  }, [enabled, load]);

  const refresh = useCallback(async () => {
    const payload = await getAiRun(selectedYear);
    setRun(payload);
    return payload;
  }, [selectedYear]);

  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const payload = await generateAiRun(selectedYear);
      setRun(payload);
      return payload;
    } catch (generateError) {
      setError(generateError);
      throw generateError;
    } finally {
      setGenerating(false);
    }
  }, [selectedYear]);

  const saveAsDraft = useCallback(async (draft) => {
    const response = await createActionPlan(draft);
    await refresh();
    return normalizeActionPlan(response);
  }, [refresh]);

  const dismiss = useCallback(async (sourceKey) => {
    const payload = await dismissAiSource(selectedYear, sourceKey);
    setRun(payload);
    return payload;
  }, [selectedYear]);

  const undoDismiss = useCallback(async (sourceKey) => {
    const payload = await undoDismissAiSource(selectedYear, sourceKey);
    setRun(payload);
    return payload;
  }, [selectedYear]);

  return {
    run,
    loading,
    generating,
    error,
    refresh,
    generate,
    saveAsDraft,
    dismiss,
    undoDismiss,
  };
}